import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, MessageSquare, Eye, Download } from 'lucide-react';
import { getCoachProfile, approveCoach, rejectCoach, requestMoreInfo } from '../services/firebase';
import DocumentViewer from '../components/verification/DocumentViewer';
import { CoachProfile } from '../types';
import toast from 'react-hot-toast';

const CoachDetails: React.FC = () => {
  const { coachId } = useParams<{ coachId: string }>();
  const navigate = useNavigate();
  const [coach, setCoach] = useState<CoachProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [requestNotes, setRequestNotes] = useState('');
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{ path: string; name: string } | null>(null);

  const loadCoachProfile = useCallback(async () => {
    if (!coachId) return;
    
    try {
      const coachData = await getCoachProfile(coachId);
      setCoach(coachData);
    } catch (error) {
      console.error('Error loading coach profile:', error);
      toast.error('Failed to load coach profile');
    } finally {
      setLoading(false);
    }
  }, [coachId]);

  useEffect(() => {
    loadCoachProfile();
  }, [loadCoachProfile]);

  const handleApprove = async () => {
    if (!coach?.id) return;
    
    try {
      const timestamp = new Date().toLocaleString();
      const adminNote = `[APPROVED - ${timestamp}] Coach verification approved by admin`;
      
      await approveCoach(coach.id, adminNote);
      toast.success('Coach approved successfully');
      loadCoachProfile(); // Reload to get updated status
    } catch (error) {
      toast.error('Failed to approve coach');
    }
  };

  const handleReject = async () => {
    if (!coach?.id || !rejectionReason.trim()) return;
    
    try {
      const timestamp = new Date().toLocaleString();
      const adminNote = `[REJECTED - ${timestamp}] ${rejectionReason}`;
      
      await rejectCoach(coach.id, rejectionReason, adminNote);
      toast.success('Coach rejected successfully');
      setShowRejectModal(false);
      setRejectionReason('');
      loadCoachProfile();
    } catch (error) {
      toast.error('Failed to reject coach');
    }
  };

  const handleRequestInfo = async () => {
    if (!coach?.id || !requestNotes.trim()) return;
    
    try {
      const timestamp = new Date().toLocaleString();
      const adminNote = `[REQUEST INFO - ${timestamp}] ${requestNotes}`;
      
      await requestMoreInfo(coach.id, adminNote);
      toast.success('Request for more information sent');
      setShowRequestModal(false);
      setRequestNotes('');
      loadCoachProfile();
    } catch (error) {
      toast.error('Failed to send request');
    }
  };

  const viewDocument = (path: string, name: string) => {
    setSelectedDocument({ path, name });
    setShowDocumentViewer(true);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-dark"></div>
      </div>
    );
  }

  if (!coach) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <p className="text-gray-500 text-lg mb-4">Coach not found</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-primary"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                {coach.firstName} {coach.lastName}
              </h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                coach.verificationStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                coach.verificationStatus === 'approved' ? 'bg-green-100 text-green-800' :
                'bg-red-100 text-red-800'
              }`}>
                {coach.verificationStatus.charAt(0).toUpperCase() + coach.verificationStatus.slice(1)}
              </span>
            </div>
            
            {/* Action buttons - show different actions based on status */}
            <div className="flex items-center space-x-2">
              {coach.verificationStatus === 'pending' && (
                <>
                  <button
                    onClick={handleApprove}
                    className="btn-primary flex items-center space-x-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    <span>Approve</span>
                  </button>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-all duration-200 font-medium flex items-center space-x-2"
                  >
                    <XCircle className="h-4 w-4" />
                    <span>Reject</span>
                  </button>
                  <button
                    onClick={() => setShowRequestModal(true)}
                    className="btn-secondary flex items-center space-x-2"
                  >
                    <MessageSquare className="h-4 w-4" />
                    <span>Request Info</span>
                  </button>
                </>
              )}
              
              {coach.verificationStatus === 'approved' && (
                <div className="flex items-center space-x-2 text-green-600">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Approved</span>
                </div>
              )}
              
              {coach.verificationStatus === 'rejected' && (
                <div className="flex items-center space-x-2 text-red-600">
                  <XCircle className="h-5 w-5" />
                  <span className="font-medium">Rejected</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Coach Information */}
          <div className="space-y-6">
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-600">Full Name</label>
                  <p className="text-gray-900">{coach.firstName} {coach.lastName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Email</label>
                  <p className="text-gray-900">{coach.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Organization</label>
                  <p className="text-gray-900">{coach.baseballOrganization}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Team</label>
                  <p className="text-gray-900">{coach.baseballTeam}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Coaching Details</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-600">Sport</label>
                  <p className="text-gray-900">{coach.sport}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Seasons</label>
                  <p className="text-gray-900">{coach.season.join(', ')}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Age Groups</label>
                  <p className="text-gray-900">{coach.ageGroup.join(', ')}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Coaching Divisions</label>
                  <p className="text-gray-900">{coach.coachingDivision.join(', ')}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Verification Details</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-600">Status</label>
                  <p className="text-gray-900">{coach.verificationStatus}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Submitted</label>
                  <p className="text-gray-900">{formatDate(coach.verificationSubmittedAt)}</p>
                </div>
                {coach.rejectionReason && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Rejection Reason</label>
                    <p className="text-gray-900">{coach.rejectionReason}</p>
                  </div>
                )}
                {coach.adminNotes && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Admin Notes</label>
                    <p className="text-gray-900">{coach.adminNotes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="space-y-6">
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Verification Documents</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600 mb-2 block">League Registration</label>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => viewDocument(coach.leagueRegistrationURL, 'League Registration')}
                      className="btn-primary flex items-center space-x-2"
                    >
                      <Eye className="h-4 w-4" />
                      <span>View</span>
                    </button>
                    <button
                      onClick={() => viewDocument(coach.leagueRegistrationURL, 'League Registration')}
                      className="btn-secondary flex items-center space-x-2"
                    >
                      <Download className="h-4 w-4" />
                      <span>Download</span>
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-600 mb-2 block">Supporting Documents</label>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => viewDocument(coach.supportingDocURL, 'Supporting Documents')}
                      className="btn-primary flex items-center space-x-2"
                    >
                      <Eye className="h-4 w-4" />
                      <span>View</span>
                    </button>
                    <button
                      onClick={() => viewDocument(coach.supportingDocURL, 'Supporting Documents')}
                      className="btn-secondary flex items-center space-x-2"
                    >
                      <Download className="h-4 w-4" />
                      <span>Download</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Document Viewer Modal */}
      {showDocumentViewer && selectedDocument && (
        <DocumentViewer
          documentPath={selectedDocument.path}
          documentName={selectedDocument.name}
          onClose={() => {
            setShowDocumentViewer(false);
            setSelectedDocument(null);
          }}
        />
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Coach</h3>
            <textarea
              placeholder="Enter rejection reason..."
              className="input-field mb-4"
              rows={4}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowRejectModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectionReason.trim()}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Info Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Request More Information</h3>
            <textarea
              placeholder="Enter notes for additional documentation needed..."
              className="input-field mb-4"
              rows={4}
              value={requestNotes}
              onChange={(e) => setRequestNotes(e.target.value)}
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowRequestModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestInfo}
                disabled={!requestNotes.trim()}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Send Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachDetails; 